/** Cmd/Ctrl+K command palette: autocomplete suggestions, hybrid search, recent docs, quick actions,
 *  project + workspace navigation, and theme / language / create-project actions */

import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { Search, FileText, Plus, X, Clock, Sparkles, History, Loader2, HardDrive, FolderKanban, Sun, Moon, Monitor, Languages, ExternalLink } from 'lucide-react'
import { API_BASE } from '../../lib/api-client'
import { useDocuments } from '../../hooks/use-documents'
import { useSearch, useSuggest } from '../../hooks/use-search'
import { useDebounce } from '../../hooks/use-debounce'
import { useAppStore } from '../../stores/app-store'
import { cn } from '../../lib/utils'
import { useProjects } from '../../hooks/use-projects'
import { useCurrentProjectSlug } from '../../hooks/use-current-project'
import { THEME_PREFERENCES, type ThemePreference } from '../../lib/theme'
import { LOCALES, useT, type Locale, type TranslationKey } from '../../i18n'
import { DOCS_NAV_ITEMS, WORKSPACE_NAV_ITEMS, projectPath } from '../shell/nav-config'

const THEME_ITEMS: Record<ThemePreference, { labelKey: TranslationKey; icon: typeof Sun }> = {
  light: { labelKey: 'theme.light', icon: Sun },
  dark: { labelKey: 'theme.dark', icon: Moon },
  system: { labelKey: 'theme.system', icon: Monitor },
}
const LOCALE_LABEL_KEYS: Record<Locale, TranslationKey> = { en: 'language.en', vi: 'language.vi' }

/** Case- and diacritic-insensitive text for client-side matching ("tai lieu" matches "Tài liệu") */
function normalize(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase()
}

export function matchesQuery(query: string, ...candidates: string[]): boolean {
  const q = normalize(query.trim())
  return !q || candidates.some((c) => normalize(c).includes(q))
}

export function CommandPalette() {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query.trim(), 250)
  const debouncedSuggestQuery = useDebounce(query.trim(), 150)

  const { theme, commandPaletteOpen: open, setCommandPaletteOpen: setOpen } = useAppStore()
  const navigate = useNavigate()

  // Autocomplete suggestions (faster debounce, >= 1 char)
  const { data: suggestData } = useSuggest(debouncedSuggestQuery)
  // Hybrid search for typed queries (>= 2 chars); a miss is recorded against the current project's Health
  const currentProject = useCurrentProjectSlug()
  const { data: searchData, isFetching: isSearching } = useSearch(debouncedQuery, 'all', currentProject)
  // Recent docs when no query
  const { data: recentData } = useDocuments({ limit: 5 })
  const t = useT()
  const { data: projects } = useProjects()
  const themePreference = useAppStore((s) => s.themePreference)
  const setThemePreference = useAppStore((s) => s.setThemePreference)
  const locale = useAppStore((s) => s.locale)
  const setLocale = useAppStore((s) => s.setLocale)
  const setCreateProjectOpen = useAppStore((s) => s.setCreateProjectOpen)

  const isDark = theme === 'dark'

  // Toggle on Ctrl+K / Cmd+K
  const toggleCommandPalette = useAppStore((s) => s.toggleCommandPalette)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
      toggleCommandPalette()
    }
    if (e.key === 'Escape') setOpen(false)
  }, [toggleCommandPalette, setOpen])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const openDocument = (doc: { slug: string }) => {
    navigate(`/doc/${encodeURIComponent(doc.slug)}`)
    setOpen(false)
    setQuery('')
  }

  /** Open upload result — navigate to file URL directly */
  const openUpload = (result: { fileUrl?: string }) => {
    if (result.fileUrl) {
      window.open(`${API_BASE}${result.fileUrl}`, '_blank')
    }
    setOpen(false)
    setQuery('')
  }

  const handleSuggestionClick = (item: { text: string; documentId?: string; slug?: string; source: string }) => {
    if (item.documentId && item.slug) {
      // Title or fuzzy suggestion — navigate directly
      openDocument({ slug: item.slug })
    } else {
      // History suggestion — fill search input to trigger full search
      setQuery(item.text)
    }
  }

  /** New documents need a description, so open the create dialog on the Documents page */
  const handleNewDoc = () => {
    setOpen(false)
    setQuery('')
    navigate('/?new=1')
  }

  /** Close the palette and run a navigation or preference action */
  const runAndClose = (action: () => void) => {
    action()
    setOpen(false)
    setQuery('')
  }

  if (!open) return null

  const suggestions = suggestData?.suggestions ?? []
  const searchResults = searchData?.results ?? []
  const recentDocs = recentData?.data ?? []
  const showSuggestions = debouncedSuggestQuery.length >= 1 && suggestions.length > 0
  const showSearch = debouncedQuery.length >= 2 && searchResults.length > 0
  const showRecent = !showSuggestions && !showSearch && recentDocs.length > 0

  // Client-side filtered navigation and preference commands
  const projectItems = (projects ?? []).filter((p) => matchesQuery(query, p.name, p.slug)).slice(0, 8)
  const workspaceItems = WORKSPACE_NAV_ITEMS.filter((item) => matchesQuery(query, t(item.labelKey), item.to))
  const docsItems = DOCS_NAV_ITEMS.filter((item) => matchesQuery(query, t(item.labelKey), 'docs'))
  const createProjectLabel = t('palette.createProject')
  const showCreateProject = matchesQuery(query, createProjectLabel, 'project')
  const themeItems = THEME_PREFERENCES.filter((value) => value !== themePreference)
    .map((value) => ({ value, ...THEME_ITEMS[value], label: t('palette.setTheme', { theme: t(THEME_ITEMS[value].labelKey) }) }))
    .filter((item) => matchesQuery(query, item.label, 'theme'))
  const localeItems = LOCALES.filter((value) => value !== locale)
    .map((value) => ({ value, label: t('palette.setLanguage', { language: t(LOCALE_LABEL_KEYS[value]) }) }))
    .filter((item) => matchesQuery(query, item.label, 'language'))
  const showPreferences = showCreateProject || themeItems.length > 0 || localeItems.length > 0

  const groupHeading = (label: string) => (
    <span className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
      {label}
    </span>
  )

  /** Icon for suggestion source type */
  const suggestIcon = (source: string) => {
    if (source === 'history') return <History className="h-3.5 w-3.5" />
    if (source === 'fuzzy') return <Sparkles className="h-3.5 w-3.5" />
    return <FileText className="h-3.5 w-3.5" />
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[10vh] md:px-0 md:pt-[20vh]"
      onClick={() => setOpen(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className={cn(
          'relative w-full max-w-lg overflow-hidden rounded-2xl border shadow-2xl',
          isDark ? 'border-white/[0.08] bg-surface-2' : 'border-neutral-200 bg-white',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          {/* Search input */}
          <div
            className={cn(
              'flex items-center gap-2 border-b px-4 py-3',
              isDark ? 'border-white/[0.06]' : 'border-neutral-200',
            )}
          >
            {isSearching ? (
              <Loader2 className={cn('h-4 w-4 shrink-0 animate-spin', isDark ? 'text-brand-400' : 'text-brand-500')} />
            ) : (
              <Search className={cn('h-4 w-4 shrink-0', isDark ? 'text-neutral-500' : 'text-neutral-400')} />
            )}
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search documents or type a command..."
              className={cn(
                'flex-1 bg-transparent text-base outline-none md:text-sm',
                isDark ? 'text-neutral-100 placeholder-neutral-500' : 'text-neutral-900 placeholder-neutral-400',
              )}
              autoFocus
            />
            <button
              onClick={() => setOpen(false)}
              className={cn(
                'cursor-pointer rounded-md p-0.5',
                isDark ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-400 hover:text-neutral-600',
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty
              className={cn('px-3 py-6 text-center text-sm', isDark ? 'text-neutral-500' : 'text-neutral-400')}
            >
              {isSearching ? 'Searching...' : 'No results found.'}
            </Command.Empty>

            {/* Quick actions */}
            <Command.Group
              heading={
                <span className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
                  Actions
                </span>
              }
            >
              <CommandItem
                value="action-new-document"
                icon={<Plus className="h-3.5 w-3.5" />}
                label={t('documents.new')}
                onSelect={handleNewDoc}
                isDark={isDark}
              />
            </Command.Group>

            {/* Autocomplete suggestions */}
            {showSuggestions && (
              <Command.Group
                heading={
                  <span className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
                    Suggestions
                  </span>
                }
              >
                {suggestions.map((item, idx) => (
                  <CommandItem
                    key={`suggest-${idx}`}
                    icon={suggestIcon(item.source)}
                    label={item.text}
                    sublabel={item.source === 'fuzzy' ? 'fuzzy match' : item.source === 'history' ? 'recent search' : undefined}
                    accuracy={item.accuracy}
                    onSelect={() => handleSuggestionClick(item)}
                    isDark={isDark}
                  />
                ))}
              </Command.Group>
            )}

            {/* Search results */}
            {showSearch && (
              <Command.Group
                heading={
                  <span className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
                    Search results
                  </span>
                }
              >
                {searchResults.map((result) => {
                  const isUpload = result.resultType === 'upload'
                  return (
                    <CommandItem
                      key={result.id}
                      icon={isUpload ? <HardDrive className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                      label={result.title}
                      sublabel={result.snippet ?? result.category ?? (isUpload ? 'storage' : undefined)}
                      accuracy={result.accuracy}
                      onSelect={isUpload ? () => openUpload(result) : () => openDocument(result)}
                      isDark={isDark}
                    />
                  )
                })}
              </Command.Group>
            )}

            {/* Recent documents (when no search query) */}
            {showRecent && (
              <Command.Group
                heading={
                  <span className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
                    Recent
                  </span>
                }
              >
                {recentDocs.map((doc) => (
                  <CommandItem
                    key={doc.id}
                    icon={<Clock className="h-3.5 w-3.5" />}
                    label={doc.title}
                    sublabel={doc.category ?? undefined}
                    onSelect={() => openDocument(doc)}
                    isDark={isDark}
                  />
                ))}
              </Command.Group>
            )}

            {/* Projects → /p/:slug */}
            {projectItems.length > 0 && (
              <Command.Group heading={groupHeading(t('palette.projects'))}>
                {projectItems.map((project) => (
                  <CommandItem
                    key={`project-${project.id}`}
                    value={`project-${project.id}`}
                    icon={<FolderKanban className="h-3.5 w-3.5" />}
                    label={project.name}
                    sublabel={project.slug}
                    onSelect={() => runAndClose(() => navigate(projectPath(project.slug)))}
                    isDark={isDark}
                  />
                ))}
              </Command.Group>
            )}

            {/* Workspace pages */}
            {workspaceItems.length > 0 && (
              <Command.Group heading={groupHeading(t('palette.workspace'))}>
                {workspaceItems.map(({ labelKey, to, icon: Icon }) => (
                  <CommandItem
                    key={`nav-${to}`}
                    value={`nav-${to}`}
                    icon={<Icon className="h-3.5 w-3.5" />}
                    label={t(labelKey)}
                    onSelect={() => runAndClose(() => navigate(to))}
                    isDark={isDark}
                  />
                ))}
              </Command.Group>
            )}

            {/* External docs (API and CLI references moved out of the app) */}
            {docsItems.length > 0 && (
              <Command.Group heading={groupHeading(t('nav.docs'))}>
                {docsItems.map(({ labelKey, to, icon: Icon }) => (
                  <CommandItem
                    key={`docs-${to}`}
                    value={`docs-${to}`}
                    icon={<Icon className="h-3.5 w-3.5" />}
                    label={t(labelKey)}
                    sublabel={t('nav.opensInNewTab')}
                    trailing={<ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />}
                    onSelect={() => runAndClose(() => window.open(to, '_blank', 'noopener,noreferrer'))}
                    isDark={isDark}
                  />
                ))}
              </Command.Group>
            )}

            {/* Create project, theme, language */}
            {showPreferences && (
              <Command.Group heading={groupHeading(t('palette.preferences'))}>
                {showCreateProject && (
                  <CommandItem
                    value="action-create-project"
                    icon={<Plus className="h-3.5 w-3.5" />}
                    label={createProjectLabel}
                    onSelect={() => runAndClose(() => setCreateProjectOpen(true))}
                    isDark={isDark}
                  />
                )}
                {themeItems.map(({ value, label, icon: Icon }) => (
                  <CommandItem
                    key={`theme-${value}`}
                    value={`theme-${value}`}
                    icon={<Icon className="h-3.5 w-3.5" />}
                    label={label}
                    onSelect={() => runAndClose(() => setThemePreference(value))}
                    isDark={isDark}
                  />
                ))}
                {localeItems.map(({ value, label }) => (
                  <CommandItem
                    key={`locale-${value}`}
                    value={`locale-${value}`}
                    icon={<Languages className="h-3.5 w-3.5" />}
                    label={label}
                    onSelect={() => runAndClose(() => setLocale(value))}
                    isDark={isDark}
                  />
                ))}
              </Command.Group>
            )}
          </Command.List>

          {/* Footer hint */}
          <div
            className={cn(
              'flex justify-end border-t px-3 py-2',
              isDark ? 'border-white/[0.06]' : 'border-neutral-200',
            )}
          >
            <span className={cn('text-[11px]', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
              ↑↓ navigate · Enter select · Esc close
            </span>
          </div>
        </Command>
      </div>
    </div>
  )
}

function AccuracyBadge({ value, isDark }: { value: number; isDark: boolean }) {
  const color =
    value >= 80
      ? isDark
        ? 'text-emerald-400 bg-emerald-500/10'
        : 'text-emerald-600 bg-emerald-50'
      : value >= 50
        ? isDark
          ? 'text-amber-400 bg-amber-500/10'
          : 'text-amber-600 bg-amber-50'
        : isDark
          ? 'text-neutral-500 bg-neutral-500/10'
          : 'text-neutral-400 bg-neutral-400/10'

  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium tabular-nums group-aria-selected:bg-white/20 group-aria-selected:text-white',
        color,
      )}
    >
      {value}%
    </span>
  )
}

function CommandItem({
  value,
  icon,
  label,
  sublabel,
  accuracy,
  trailing,
  onSelect,
  isDark,
}: {
  /** Unique cmdk value; defaults to the item's text, which can collide across groups */
  value?: string
  icon: React.ReactNode
  label: string
  sublabel?: string
  accuracy?: number
  trailing?: React.ReactNode
  onSelect: () => void
  isDark: boolean
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className={cn(
        'group flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm aria-selected:bg-brand-600 aria-selected:text-white',
        isDark ? 'text-neutral-300 hover:bg-surface-3' : 'text-neutral-700 hover:bg-neutral-50',
      )}
    >
      <span className="shrink-0 text-neutral-500 aria-selected:text-white">{icon}</span>
      <div className="flex-1 truncate">
        <span>{label}</span>
        {sublabel && (
          <span className={cn('ml-2 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>{sublabel}</span>
        )}
      </div>
      {accuracy != null && <AccuracyBadge value={accuracy} isDark={isDark} />}
      {trailing}
    </Command.Item>
  )
}
